<template>
    <vx-expandable-card>
        <template v-slot:header>Group Status</template>

        <template v-if="!currentStatus" v-slot:content>
            <page-loading class="py-8"></page-loading>
        </template>

        <template v-else v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <vx-radio-selection v-model="selected" :disabled="!props.isEditable">
                    <vx-radio-selection-item
                        v-for="option in statusOptions"
                        :key="option.value"
                        :value="option.value"
                        :disabled="isOptionDisabled(option.value)"
                        :data-test="`group-status-option-${option.value}`"
                    >
                        <template v-slot:header>
                            <span class="capitalize">{{ option.value }}</span>
                        </template>
                        <template v-slot:content>
                            <div>{{ option.description }}</div>
                            <div
                                v-if="option.value === 'closed' && closedBlockedByBilling"
                                class="mt-2 text-xs text-rose-800"
                                data-test="closed-billing-block"
                            >
                                Groups with active billing cannot be closed. Billing must be disabled first.
                            </div>
                        </template>
                    </vx-radio-selection-item>
                </vx-radio-selection>

                <div v-if="props.isEditable" class="flex gap-4 items-center">
                    <vx-button
                        size="lg"
                        class="grow"
                        :disabled="!canSubmit"
                        :loading="submitting"
                        @click.prevent="onPrimaryClick"
                        data-test="group-status-submit"
                    >
                        Update Now
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { VxButton, VxExpandableCard, VxRadioSelection, VxRadioSelectionItem, VxSwal } from '@voxie/frontend-components';
import PageLoading from '~/components/general/PageLoading.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminGroupsClient } from '~/services';
import {
    GROUP_STATUS_ACTIVE,
    GROUP_STATUS_ALLOWED_TRANSITIONS,
    GROUP_STATUS_CLOSED,
    GROUP_STATUS_DRAFT,
    GROUP_STATUS_SUSPENDED,
    type GroupStatus,
} from '~/constants/groupStatuses';

const props = withDefaults(
    defineProps<{
        teamId: number;
        groupId: string;
        isEditable?: boolean;
    }>(),
    {
        isEditable: true,
    }
);

const statusOptions: { value: GroupStatus; description: string }[] = [
    {
        value: GROUP_STATUS_DRAFT,
        description: 'Group is not yet operational. Assign phone numbers and activate when ready.',
    },
    {
        value: GROUP_STATUS_ACTIVE,
        description: 'Group is operational and can send messages.',
    },
    {
        value: GROUP_STATUS_SUSPENDED,
        description: 'Group is temporarily paused. Activity is halted until reactivated.',
    },
    {
        value: GROUP_STATUS_CLOSED,
        description: 'Group is permanently closed. Phone numbers are released and cannot be recovered.',
    },
];

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const { group, refreshGroup } = useGroupDetails();

const selected = ref<GroupStatus>(GROUP_STATUS_DRAFT);
const submitting = ref(false);

const currentStatus = computed<GroupStatus | undefined>(() => group.value?.status);

watch(
    currentStatus,
    (value) => {
        if (value) {
            selected.value = value;
        }
    },
    { immediate: true }
);

const closedBlockedByBilling = computed(() => Boolean(group.value?.is_billable));

const isOptionDisabled = (target: GroupStatus) => {
    if (!currentStatus.value) {
        return true;
    }
    if (target === currentStatus.value) {
        return false;
    }
    if (target === GROUP_STATUS_CLOSED && closedBlockedByBilling.value) {
        return true;
    }
    const allowed = GROUP_STATUS_ALLOWED_TRANSITIONS[currentStatus.value] ?? [];
    return !allowed.includes(target);
};

const canSubmit = computed(
    () => props.isEditable && !!currentStatus.value && selected.value !== currentStatus.value && !submitting.value
);

const onPrimaryClick = () => {
    if (!canSubmit.value) {
        return;
    }

    if (selected.value === GROUP_STATUS_CLOSED) {
        VxSwal.confirmation(
            'Close Group',
            'Closing this group will release all assigned phone numbers. Released numbers cannot be recovered by this group. This action cannot be undone.',
            'danger',
            () => submitImmediate()
        );
        return;
    }

    submitImmediate();
};

const submitImmediate = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    try {
        await adminGroupsClient.updateStatus(props.teamId, props.groupId, {
            status: selected.value,
        });
        await refreshGroup(props.teamId, props.groupId);
        toasted.success('Group status updated successfully');
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
